import {
    Component,
    Element,
    Host,
    Listen,
    Method,
    Prop,
    Watch,
    h,
} from "@stencil/core";

@Component({
    tag: "ux-iws-shop-machine-check-flap",
    styleUrl: "shop-machine-check-flap.scss",
})
export class ShopMachineCheckFlap {
    @Element() domNode: HTMLUxIwsShopMachineCheckFlapElement;

    /** true: show slot "content", false: show slot "flap" */
    @Prop({ mutable: true, reflect: true }) open?: boolean = false;
    /** true: layer position near domElement, false: layer position fixed */
    @Prop({ mutable: true, reflect: true }) initial?: boolean = false;
    /** timeout to hide content in ms */
    @Prop({ mutable: true, reflect: true }) autoClose?: number;
    /** hide content on document scroll */
    @Prop() readonly scrollClose?: boolean = false;
    /** hide content on click anywhere in the document */
    @Prop() readonly clickClose?: boolean = false;

    private baseClass: string = "ux-iws-shop-machine-check-flap";
    /** timer for auto-close */
    private showTimer: NodeJS.Timeout;
    private setShowTimeout(timeout: number): void {
        this.clearShowTimer();
        this.showTimer = setTimeout(this.hideContent.bind(this), timeout);
    }
    private clearShowTimer(): void {
        if (this.showTimer) {
            clearTimeout(this.showTimer);
            this.showTimer = null;
        }
    }

    /** show content, optionally give a timeout for autoClose */
    @Method()
    async showContent(timeout?: number): Promise<void> {
        this.clearShowTimer();
        this.open = true;
        if (timeout !== undefined) {
            this.autoClose = timeout;
        }
    }

    /** hide content */
    @Method()
    async hideContent(): Promise<void> {
        this.clearShowTimer();
        this.open = false;
        this.initial = false;
    }

    /** toggle show/hide initial layer position, optionally give a boolean to force visibility */
    @Method()
    async toggleInitial(force?: boolean): Promise<void> {
        const { initial } = this;
        this.initial = force !== undefined ? !!force : !initial;
        if (this.initial) this.open = true;
    }

    /** hide fixed content on document scroll */
    @Listen("scroll", { target: "document" })
    onScroll() {
        if (this.scrollClose && this.open && !this.initial) {
            this.hideContent();
        }
    }

    /** toggle visibility on element click */
    private onClick() {
        if (this.open) {
            this.hideContent();
        } else {
            this.showContent();
        }
    }

    /** hide fixed content on click anywhere in the document */
    @Listen("click", { target: "document" })
    onOuterClick(e: MouseEvent) {
        const target = e.target as HTMLElement;
        const isInside = target.closest(`.${this.baseClass}`) === this.domNode; // filter clicks (alternatively, run e.stopPropagation() on component-click-listener)
        if (!isInside) {
            if (this.clickClose && this.open && !this.initial) {
                this.hideContent();
            }
        }
    }

    @Watch("open")
    watchOpenHandler(newValue: boolean) {
        if (newValue && this.autoClose) {
            this.setShowTimeout(this.autoClose);
        }
    }

    componentDidLoad() {
        if (this.open && this.autoClose) {
            this.setShowTimeout(this.autoClose);
        }
    }
    disconnectedCallback() {
        this.clearShowTimer();
    }

    render() {
        return (
            <Host
                class={{
                    [this.baseClass]: true,
                    [`${this.baseClass}--initial`]: this.initial,
                    [`${this.baseClass}--open`]: this.open,
                }}
                onClick={this.onClick}
            >
                <div
                    class={`ux-u-shadow ux-u-background-white ${this.baseClass}__layer`}
                >
                    <div
                        class={`${this.baseClass}__flap ux-u-text-uppercase ux-text-small ux-text-small--bold ux-u-p-xxxs ux-u-py-xxs`}
                    >
                        <slot name="flap"></slot>
                    </div>
                    <div
                        class={`${this.baseClass}__content ux-u-p-xs ux-u-w-100`}
                    >
                        <slot name="content"></slot>
                    </div>
                </div>
            </Host>
        );
    }
}
